"use client";

import { useCallback } from "react";
import type { BackendInputWithLabelProps } from "../../backend-input";
import { BackendInputWithLabel } from "../../backend-input";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { useController, useFormContext } from "react-hook-form";

// Spezielle Text Input Komponente für einfache Text-Eingaben
export function ControlledTextInput<
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  control,
  onChange,
  onBlur,
  onFieldBlur,
  ...props
}: Omit<
  BackendInputWithLabelProps,
  "value" | "onChange" | "onBlur" | "error" | "name"
> & {
  name: FieldPath<TFieldValues>;
  control?: Control<TFieldValues>;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFieldBlur?: (fieldName: string) => void;
  unit?: string;
}) {
  const formContext = useFormContext<TFieldValues>();
  const actualControl = control || formContext?.control;

  if (!actualControl) {
    throw new Error(
      "Controlled text input must be used inside FormProvider or with control prop"
    );
  }

  const {
    field,
    fieldState: { error },
  } = useController({ name, control: actualControl });

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      field.onChange(e.target.value);
      onChange?.(e);
    },
    [field, onChange]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      field.onBlur();
      onBlur?.(e);
      onFieldBlur?.(name);
    },
    [field, onBlur, onFieldBlur, name]
  );

  return (
    <BackendInputWithLabel
      {...props}
      value={field.value ?? ""}
      onChange={handleChange}
      onBlur={handleBlur}
      error={error?.message}
      unit={props.unit}
      readOnly={props.readOnly}
    />
  );
}
